import { useEffect, useMemo, useState } from 'react';
import { ACHIEVEMENT_BY_CODE, THEMES, isHunt, type PublicPlayer, type RoundResultRow } from '@numslearn/shared';
import { useSettings } from '../settings.js';
import { BoardView } from '../game/BoardView.js';
import { BadgeBurst } from '../game/BadgeBurst.js';
import { Praise } from '../game/Praise.js';
import { ruleLine, verdictLine } from '../game/verdict.js';
import { navigate } from '../router.js';
import type { SessionApi } from '../session/useSession.js';
import type { StringKey } from '../i18n/index.js';

/**
 * The student's screen during a class session.
 *
 * Same board, same themes as solo practice — but every tap is answered by the
 * server, and a live class strip runs under the HUD so a child can see where
 * they stand without looking up at the projector.
 */
export function PlayLive({ session }: { session: SessionApi }) {
  const { t, num, themeId } = useSettings();
  const theme = THEMES[themeId];
  const { snapshot, board, round, results } = session;
  const [queue, setQueue] = useState<string[]>([]);

  useEffect(() => {
    if (!session.newBadges.length) return;
    setQueue((q) => [...q, ...session.newBadges]);
    session.clearBadges();
  }, [session]);

  const me = useMemo(
    () => snapshot?.players.find((p) => p.id === session.playerId) ?? null,
    [snapshot, session.playerId],
  );

  if (!snapshot) {
    return <div className="live-wait"><p>{t('live.reconnecting')}</p></div>;
  }

  const phase = snapshot.phase;
  const playing = phase === 'round_active' && board !== null;

  const ev = session.lastEvent;
  const cfg = round?.config ?? null;
  // The same verdict builder the solo game uses, so a rule cannot be worded
  // one way in practice and another way in a lesson.
  const line = cfg
    ? verdictLine(ev, { cfg, t, num, targetsLeft: board?.targetsLeft ?? 0 })
    : '';
  const hunt = cfg ? isHunt(cfg) : false;

  return (
    <div className="surface" data-art={theme.id} data-sel={theme.selectionStyle}>
      <header className="game-head">
        <div className="game-title">
          <span className="game-grade">
            {snapshot.className} · {round ? t('live.round', { a: round.index, b: round.total }) : t('host.lobby')}
          </span>
          {round && (
            <h1 className="game-rule">
              {ruleLine(round.config, t)}
            </h1>
          )}
        </div>
        <button className="tb-btn" onClick={() => {
          if (window.confirm(t('live.leaveAsk'))) { session.leave(); navigate('/'); }
        }}>{t('nav.leave')}</button>
      </header>

      {phase === 'lobby' && (
        <div className="live-panel">
          <strong>{t('live.lobby')}</strong>
          <p>{t('live.lobbyHint')}</p>
          <p className="live-count">{t('live.players', { n: snapshot.players.length })}</p>
        </div>
      )}
      {phase === 'countdown' && <div className="live-panel is-loud"><strong>{t('live.countdown')}</strong></div>}
      {phase === 'round_paused' && <div className="live-panel"><strong>{t('live.paused')}</strong></div>}

      {(phase === 'round_results' || phase === 'ended') && results && (
        <Results
          rows={results}
          phase={phase}
          playerId={session.playerId}
          teacherName={snapshot.teacherName}
        />
      )}

      {playing && board && (
        <>
          <div className="hud">
            {snapshot.secondsLeft !== null && (
              <Stat label={t('hud.time')} value={num(snapshot.secondsLeft)} warn={snapshot.secondsLeft <= 10} />
            )}
            <Stat label={t('hud.score')} value={num(board.score)} />
            <Stat label={t('hud.combo')} value={`×${num(board.combo)}`} />
            {hunt
              ? <Stat label={t('hud.found')} value={num(board.targetsLeft)} />
              : <Stat label={t('live.rank')} value={num(me?.rank ?? 0)} />}
            {hunt && <Stat label={t('live.rank')} value={num(me?.rank ?? 0)} />}
            <div className="meter" aria-hidden="true">
              <i style={{ width: `${Math.min(100, board.combo * 10)}%` }} />
            </div>
          </div>

          <p className="equation" aria-live="polite" data-kind={ev?.kind ?? 'none'}>{line}</p>

          <div className="stage-wrap">
            <BoardView
              board={{ tiles: board.tiles, rows: board.rows, cols: board.cols }}
              config={cfg!}
              selected={session.selected}
              hintWhere={session.hintWhere}
              lastEvent={session.lastEvent}
              onTap={session.tap}
              disabled={!playing}
              seed={round?.seed ?? 0}
            />
            <Praise
              nonce={ev?.kind === 'match' ? ev.nonce : 0}
              level={cfg?.celebration ?? 'word'}
              combo={board.combo}
              t={t}
            />
          </div>

          <div className="acts">
            {cfg && cfg.adds > 0 && (
            <button className="gbtn" onClick={session.addRow} disabled={board.addsLeft === 0}>
              {t('btn.add')} <span className="cnt">{num(board.addsLeft)}</span>
            </button>
            )}
            <button className="gbtn" onClick={session.useHint} disabled={board.hintsLeft === 0}>
              {t('btn.hint')} <span className="cnt">{num(board.hintsLeft)}</span>
            </button>
          </div>
        </>
      )}

      <LiveStrip players={snapshot.players} me={session.playerId} />
      <BadgeBurst queue={queue} onShown={(code) => setQueue((q) => q.filter((c) => c !== code))} />
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`stat${warn ? ' warn' : ''}`}>
      <span className="stat-k">{label}</span>
      <span className="stat-v">{value}</span>
    </div>
  );
}

/** The class, ranked, as a strip a child can read at a glance. */
function LiveStrip({ players, me }: { players: PublicPlayer[]; me: string | null }) {
  const { num } = useSettings();
  return (
    <ol className="strip">
      {players.slice(0, 12).map((p) => (
        <li key={p.id} className={`strip-row${p.id === me ? ' is-me' : ''}${p.connected ? '' : ' is-off'}`}>
          <span className="strip-rank">{num(p.rank)}</span>
          <span className="strip-name">{p.nickname}</span>
          <span className="strip-bar" aria-hidden="true"><i style={{ width: `${Math.round(p.progress * 100)}%` }} /></span>
          <span className="strip-score">{num(p.score)}</span>
        </li>
      ))}
    </ol>
  );
}

function Results({ rows, phase, playerId, teacherName }: {
  rows: RoundResultRow[];
  phase: string;
  playerId: string | null;
  teacherName: string;
}) {
  const { t, num } = useSettings();
  return (
    <div className="live-panel is-results">
      <strong>{phase === 'ended' ? t('live.final') : t('live.results')}</strong>
      {/* The results screen is the one most likely to be shown to a parent or
          photographed off a projector, so it says whose class it is. */}
      {teacherName && <span className="res-who">{t('class.of', { name: teacherName })}</span>}
      <ol className="result-list">
        {rows.map((r) => (
          <li key={r.playerId} className={r.playerId === playerId ? 'is-me' : ''}>
            <span className="res-rank" data-rank={r.rank}>{num(r.rank)}</span>
            <span className="res-name">{r.nickname}{r.playerId === playerId ? ` · ${t('live.you')}` : ''}</span>
            <span className="res-badges">
              {r.newBadges.map((c) => (
                <span key={c} title={c}>{ACHIEVEMENT_BY_CODE.get(c)?.icon ?? '★'}</span>
              ))}
            </span>
            <span className="res-score">{num(r.roundScore)}</span>
          </li>
        ))}
      </ol>
      {phase !== 'ended' && <p className="live-count">{t('live.waitingNext')}</p>}
    </div>
  );
}
